import axios from '@/api/axios';
import { gql } from '@/api/utils';

const productBundleSidebarElementFragment = gql`
productBundleSidebarElement {
  title
  totalPriceLabel
  totalSavingsLabel
  originalPriceLabel
  buttonText
  bundleProducts {
    productId
    title
    retailPrice
    discountPrice
    savingAmount
    image {
      original
      thumbnail
    }
  }
}`;

export default {
  async getGroups(cityId, productType = 'DESIGNER_TAG') {
    try {
      const query = gql`
        query productGroups($cityId: ID!, $productType: ProductType) {
          productGroups(cityId: $cityId, type: $productType) {
            id
            name
            description
            price
            image {
              original
            }
            images {
              original
            }
            mobileImage {
              original
            }
            mobileImages {
              original
            }
            municipalGroup
            sort(cityId: $cityId)
            productStyles(cityId: $cityId) {
              id
              code
              name
              keywords
              description
              price
              discountedPrice
              priceRange {
                min
                max
              }
              image {
                original
                thumbnail
              }
              disclaimer
            }
          }
        }
      `;

      const variables = { cityId, productType };
      const { productGroups: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async getGroupsWithStyles(cityId, productType = 'DESIGNER_TAG') {
    try {
      const query = gql`
        query productGroups($cityId: ID!, $productType: ProductType) {
          productGroups(cityId: $cityId, type: $productType) {
            id
            name
            description
            price
            image {
              original
            }
            images {
              original
            }
            mobileImage {
              original
            }
            mobileImages {
              original
            }
            productType
            municipalGroup
            sort(cityId: $cityId)
            productStyles(cityId: $cityId) {
              id
              code
              name
              keywords
              description
              price
              discountedPrice
              priceRange {
                min
                max
              }
              designs {
                id
                title
                designCategory
                qualification
                designSVGFile {
                  original: webPath
                }
                designText1PositionOffsetX
                designText1PositionOffsetY
                designText1BoundaryPadding
                designText1Color
                designText1HeightScaleFactor
                designText2
                designText2PositionOffsetX
                designText2PositionOffsetY
                designText2BoundaryPadding
                designText2Color
                designText2HeightScaleFactor
              }
              image {
                original
                thumbnail
                imageAltText
              }
              images {
                id
                original
                imageAltText
              }
              disclaimer
              productType
              products(cityId: $cityId) {
                id
                type
                name
                __typename
                ... on CollarProduct {
                  size
                  color
                  price
                  discountedPrice
                  status
                  image {
                    original
                  }
                  backImage {
                    original
                  }
                  sizeTitle
                  sizeLength
                  sizeWidth
                  sizeDescription
                  sizeImage {
                    original
                  }
                  personalization {
                    price
                  }
                  price
                  discountedPrice
                }
                ... on LeashProduct {
                  width
                  length
                  color
                  price
                  discountedPrice
                  status
                  image {
                    original
                  }
                  backImage {
                    original
                  }
                  personalization {
                    price
                  }
                  price
                  discountedPrice
                }
                ... on PetEssentialsProduct {
                  status
                  color
                  size
                  price
                  discountedPrice
                  image {
                    original
                  }
                  maxQuantity
                  image {
                    original
                  }
                }
              }
            }
          }
        }
      `;

      const variables = { cityId, productType };
      const { productGroups: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async getGroup(id, cityId) {
    try {
      const query = gql`
        query productGroup($id: ID!, $cityId: ID!) {
          productGroup(id: $id) {
            id
            name
            description
            price
            image {
              original
            }
            images {
              original
            }
            mobileImage {
              original
            }
            mobileImages {
              original
            }
            productType
            municipalGroup
            sort(cityId: $cityId)
            productStyles(cityId: $cityId) {
              id
              code
              name
              price
              discountedPrice
              priceRange {
                min
                max
              }
              disclaimer
              image {
                thumbnail
              }
            }
            disclaimer
          }
        }
      `;

      const variables = { id, cityId };
      const { productGroup: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async getReplacementTagStyle(productStyleId, cityId, petId) {
    try {
      const query = gql`
        query Query($productStyleId: ID!, $cityId: ID!, $petId: ID!) {
          replacementTags: replacementTag(productStyleId: $productStyleId, petId: $petId) {
            id
            code
            name
            description
            disclaimer
            price
            discountedPrice
            priceRange {
              min
              max
            }
            image {
              original
              imageAltText
            }
            images {
              id
              original
              imageAltText
            }
            productType
            products(cityId: $cityId) {
              id
              type
              name
              __typename
              ... on DesignerTagProduct {
                size
                color
                status
                price
                discountedPrice
                personalization {
                  price
                }
                image {
                  original
                  imageAltText
                }
                backImage {
                  original
                  imageAltText
                }
                keyChainPrice
                keyChainDiscountedPrice
                municipalTag
              }
            }
            ${productBundleSidebarElementFragment}
          }
          connectors: products(cityId: $cityId, type: CONNECTOR) {
            id
            name
            description
            status
            price
            image {
              original
            }
          }
        }
      `;

      const variables = { productStyleId, cityId, petId };
      return await axios.post(`/`, { query, variables });
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async getStyle(id, cityId) {
    try {
      const query = gql`
        query Query($id: ID!, $cityId: ID!) {
          productStyle: productStyle(id: $id) {
            ${productBundleSidebarElementFragment}
            id
            code
            name
            description
            disclaimer
            price
            discountedPrice
            priceRange {
              min
              max
            }
            designs {
              id
              title
              designCategory
              qualification
              designSVGFile {
                original: webPath
              }
              designText1PositionOffsetX
              designText1PositionOffsetY
              designText1BoundaryPadding
              designText1Color
              designText1HeightScaleFactor
              designText2
              designText2PositionOffsetX
              designText2PositionOffsetY
              designText2BoundaryPadding
              designText2Color
              designText2HeightScaleFactor
            }
            image {
              original
              imageAltText
            }
            images {
              id
              original
              imageAltText
            }
            productType
            products(cityId: $cityId) {
              id
              type
              name
              __typename
              ... on DesignerTagProduct {
                size
                color
                status
                price
                discountedPrice
                personalization {
                  price
                }
                image {
                  original
                  imageAltText
                }
                backImage {
                  original
                  imageAltText
                }
                laserPrinterJig {
                  shape
                  material
                }
                material {
                  material
                }
                keyChainPrice
                keyChainDiscountedPrice
                municipalTag
              }
            }
          }
          connectors: products(cityId: $cityId, type: CONNECTOR) {
            id
            name
            description
            status
            price
            image {
              original
            }
          }
        }
      `;

      const variables = { id, cityId };
      const results = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async getProductByIDs(ids) {
    try {
      let query = gql`
        query {
      `;

      ids.map((id) => {
        query += gql`
          Product_${id}: product(id: ${id}) {
            id
            name
            price
            image {
              original
            }
            type
            ... on PetEssentialsProduct {
              discountedPrice
            }
            status
          }
        `;
      });

      query = query + gql`}`;

      return await axios.post(`/`, { query });
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async getProductsByTypes(types, cityId) {
    String.prototype.toProperCase = function () {
      return this.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    };

    try {
      let query = gql`
        query productsByType($cityId: ID!){
      `;

      types.map((type) => {
        query += gql`
          ${type?.toProperCase()}_Products: products(cityId: $cityId, type: ${type}) {
            id
            name
            price
            image {
              original
            }
            type
            ... on OtherProduct {
              discountedPrice
            }
          }
        `;
      });

      query = query + gql`}`;

      return await axios.post(`/`, { query, variables: { cityId } });
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async getWalletCardCustomization(input) {
    try {
      const query = gql`
        query getWalletCardCustomization($input: WalletCardCustomizationInput!) {
          getWalletCardCustomization(input: $input) {
            additionalCardsHtml
            placeholders {
              type {
                valueType
              }
              value
            }
            products {
              id
              ... on WalletCardProduct {
                primaryColor
                secondaryColor
                duplicateDiscountedAmount
                type
                name
                templateFields {
                  valueType
                  side
                  valueX
                  valueY
                  valueWidth
                  valueHeight
                  valueCharacterLimit
                  valueCharacterLimitPerLine
                  valueFontSize
                  valueFontWeight
                  labelName
                  labelX
                  labelY
                  labelWidth
                  labelHeight
                  labelFontSize
                  labelFontWeight
                  labelTextAlignment
                }
                frontDesign {
                  id
                  original
                  thumbnail
                }
                backDesign {
                  id
                  original
                  thumbnail
                }
              }
            }
            petTemperaments {
              name
              displayName
            }
            petPhotos {
              id
              original
              thumbnail
            }
          }
        }
      `;

      const variables = {
        input,
      };

      const { getWalletCardCustomization: results } = await axios.post('/', {
        query,
        variables,
      });
      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({ error: error.message });
    }
  },
  async getProductBundleSidebarElement(productStyleId, productId, includePersonalization) {
    try {
      const query = gql`
        query storeProductStyleScreenContextQuery($input: StoreProductStyleScreenContextInput!) {
          storeProductStyleScreenContext(input: $input) {
            ${productBundleSidebarElementFragment}
          }
        }
      `;

      const input = {
        productStyleId,
        productId,
        includePersonalization,
      };

      const variables = {
        input,
      };

      const { storeProductStyleScreenContext: results } = await axios.post('/', {
        query,
        variables,
      });

      return results;
    } catch (error) {
      return Promise.reject({ error: error.message });
    }
  },
};
